import { Injectable } from '@angular/core';
import { PackagingComponentTypesEnum } from 'src/app/model/packaging-component-types-enum';
import { PackagingPart } from 'src/app/model/packaging-part-enum';

export enum RelevantAnalysisType {
  Recyclability,
  Lifecycle,
  CostCalculation,
  PPWR,
}

export class RelevantAnalysisTypes {
  public recyclingRelevant: boolean = false;
  public lcaRelevant: boolean = false;
  public ccRelevant: boolean = false;
  public ppwrRelevant: boolean = false;

  constructor(recyclingRelevant: boolean, lcaRelevant: boolean, ccRelevant: boolean, ppwrRelevant: boolean = false) {
    this.recyclingRelevant = recyclingRelevant;
    this.lcaRelevant = lcaRelevant;
    this.ccRelevant = ccRelevant;
    this.ppwrRelevant = ppwrRelevant;
  }

  public equals(relevantAnalysisTypes: RelevantAnalysisTypes): boolean {
    return (
      this.recyclingRelevant === relevantAnalysisTypes.recyclingRelevant &&
      this.lcaRelevant === relevantAnalysisTypes.lcaRelevant &&
      this.ccRelevant === relevantAnalysisTypes.ccRelevant &&
      this.ppwrRelevant === relevantAnalysisTypes.ppwrRelevant
    );
  }
}

class RelevantField {
  public fieldName: string;
  public relevantTypes: RelevantAnalysisTypes;
  public docRestrictedToSubtypes?: PackagingComponentTypesEnum[];

  constructor(fieldName: string, relevantTypes: RelevantAnalysisTypes, docRestrictedToSubtypes?: PackagingComponentTypesEnum[]) {
    this.fieldName = fieldName;
    this.relevantTypes = relevantTypes;
    this.docRestrictedToSubtypes = docRestrictedToSubtypes;
  }
}

@Injectable({ providedIn: 'root' })
export class RelevantAnalysisService {
  relevantSystemFields: RelevantField[] = [
    new RelevantField('distributionLocations', new RelevantAnalysisTypes(true, true, true, false)),
    new RelevantField('assemblyLocation', new RelevantAnalysisTypes(false, true, false, false)),
    new RelevantField('brandName', new RelevantAnalysisTypes(false, false, false, true)),
    new RelevantField('productName', new RelevantAnalysisTypes(false, false, false, true)),
    new RelevantField('articleNumber', new RelevantAnalysisTypes(false, false, false, true)),
    new RelevantField('gtin', new RelevantAnalysisTypes(false, false, false, true)),
  ];

  relevantUnitFields: RelevantField[] = [
    new RelevantField('distributionLocations', new RelevantAnalysisTypes(true, true, true, false)),
    new RelevantField('assemblyLocation', new RelevantAnalysisTypes(false, true, false, false)),
    new RelevantField('brandName', new RelevantAnalysisTypes(false, false, false, true)),
    new RelevantField('productName', new RelevantAnalysisTypes(false, false, false, true)),
    new RelevantField('articleNumber', new RelevantAnalysisTypes(false, false, false, true)),
    new RelevantField('gtin', new RelevantAnalysisTypes(false, false, false, true)),
    new RelevantField('fillingGoodType', new RelevantAnalysisTypes(false, false, false, true)),
  ];

  relevantComponentFields: RelevantField[] = [
    new RelevantField('packagingComponentSubtype', new RelevantAnalysisTypes(true, true, true, true)),
    new RelevantField('manufacturingLocation', new RelevantAnalysisTypes(false, true, false, false)),
    new RelevantField('length', new RelevantAnalysisTypes(true, true, true, false)),
    new RelevantField('width', new RelevantAnalysisTypes(true, true, true, false)),
    new RelevantField('height', new RelevantAnalysisTypes(true, true, true, false)),
    new RelevantField('isPhysicallyAttached', new RelevantAnalysisTypes(true, true, true, false)),
    new RelevantField('removabilityCondition', new RelevantAnalysisTypes(true, true, true, false)),
    new RelevantField('isAdhesiveUsed', new RelevantAnalysisTypes(true, true, true, false)),
    new RelevantField('separationThroughCollectionAndSorting', new RelevantAnalysisTypes(true, true, true, false)),
    new RelevantField('removedByConsumer', new RelevantAnalysisTypes(true, true, true, false)),
    new RelevantField('removedBeforeUsage', new RelevantAnalysisTypes(true, true, true, false)),
    new RelevantField('isRigidComponent', new RelevantAnalysisTypes(true, true, true, true)),
    new RelevantField('surfaceRatio', new RelevantAnalysisTypes(true, true, true, false)),
    new RelevantField('hasDirectContactWithProduct', new RelevantAnalysisTypes(false, false, false, true)),
  ];

  relevantMaterialFields: RelevantField[] = [
    new RelevantField('function', new RelevantAnalysisTypes(true, true, true, true)),
    new RelevantField('material', new RelevantAnalysisTypes(true, true, true, true)),
    new RelevantField('materialManifestation', new RelevantAnalysisTypes(true, true, true, true)),
    new RelevantField('mass', new RelevantAnalysisTypes(true, true, true, false)),
    new RelevantField('color', new RelevantAnalysisTypes(true, true, true, false)),
    new RelevantField('manufacturingType', new RelevantAnalysisTypes(false, true, false, true)),
    new RelevantField('density', new RelevantAnalysisTypes(true, true, true, false)),
    new RelevantField('recyclingMaterialPercentage', new RelevantAnalysisTypes(false, true, true, true)),
    new RelevantField('materialOrigin', new RelevantAnalysisTypes(false, true, false, false)),
  ];

  isFieldRelevantForCalculations(
    relevantTypes: RelevantAnalysisTypes,
    packagingPart: PackagingPart,
    fieldName: string,
    subtype?: PackagingComponentTypesEnum
  ): boolean {
    switch (packagingPart) {
      case PackagingPart.System:
        return this.isRelevantField(relevantTypes, fieldName, this.relevantSystemFields);
      case PackagingPart.Unit:
        return this.isRelevantField(relevantTypes, fieldName, this.relevantUnitFields);
      case PackagingPart.Component:
        return this.isRelevantField(relevantTypes, fieldName, this.relevantComponentFields, subtype);
      case PackagingPart.Material:
        return this.isRelevantField(relevantTypes, fieldName, this.relevantMaterialFields);
      default:
        return false;
    }
  }

  private haveMatchingTypes(relevantTypes: RelevantAnalysisTypes, usedTypes: RelevantAnalysisTypes): boolean {
    return (
      (relevantTypes.ccRelevant && usedTypes.ccRelevant) ||
      (relevantTypes.lcaRelevant && usedTypes.lcaRelevant) ||
      (relevantTypes.recyclingRelevant && usedTypes.recyclingRelevant) ||
      (relevantTypes.ppwrRelevant && usedTypes.ppwrRelevant)
    );
  }

  private isRelevantField(
    relevantTypes: RelevantAnalysisTypes,
    fieldName: string,
    packagingPartFields: RelevantField[],
    subtype?: PackagingComponentTypesEnum
  ): boolean {
    for (const relevantField of packagingPartFields) {
      if (relevantField.fieldName === fieldName) {
        let effectiveFieldTypes = relevantField.relevantTypes;
        if (relevantField.docRestrictedToSubtypes &&
            (subtype === undefined || !relevantField.docRestrictedToSubtypes.includes(subtype))) {
          effectiveFieldTypes = new RelevantAnalysisTypes(
            relevantField.relevantTypes.recyclingRelevant,
            relevantField.relevantTypes.lcaRelevant,
            relevantField.relevantTypes.ccRelevant,
            relevantField.relevantTypes.ppwrRelevant
          );
        }
        return this.haveMatchingTypes(effectiveFieldTypes, relevantTypes);
      }
    }
    return false;
  }
}
